import { useState } from "react";
import { Link } from "react-router";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { VerifyMfaForm } from "./verify-mfa-form";
import { ReauthenticateUser } from "./reauthenticate-user";
import { useSetTotpMfa, useEnrolTotpMfa } from "../../hooks/use-mfa";
import { useTimeout } from "../../hooks/use-timeout";
import { QRCodeSVG } from "qrcode.react";

const codeSchema = yup
    .object({
        code: yup
            .string()
            .required("Please enter the code")
            .matches(/^\d{6}$/, "Enter the 6-digit code"),
    })
    .required();

export const SetTotpMfa = () => {
    const [reauthenticated, setReauthenticated] = useState(false);
    const [totpSetup, setTotpSetup] = useState(null);
    const [enrolled, setEnrolled] = useState(false);

    useTimeout(reauthenticated, setReauthenticated);

    const codeMethods = useForm({
        resolver: yupResolver(codeSchema),
    });

    const setMutation = useSetTotpMfa(setTotpSetup);

    const enrolMutation = useEnrolTotpMfa(setReauthenticated, () =>
        setEnrolled(true),
    );

    const onStartSetup = () => {
        setMutation.mutate();
    };

    const onVerify = (data) => {
        enrolMutation.mutate({ secret: totpSetup.secret, code: data.code });
    };

    if (reauthenticated) {
        return (
            <div className="grid grid-cols-12 items-start gap-y-6">
                <Card size="lg" className="grid grid-cols-12 gap-y-8">
                    <div className="col-span-full">
                        <h2 className="mb-3 text-xl font-semibold">
                            Set up authenticator app
                        </h2>
                    </div>

                    {enrolled ? (
                        <div className="col-span-full">
                            <p className="text-sm/6">
                                Multi-factor authentication is now enabled.
                            </p>
                        </div>
                    ) : totpSetup ? (
                        <>
                            <div className="col-span-full">
                                <p className="mb-4 text-sm/6">
                                    Scan the QR code below with your
                                    authenticator app, then enter the 6-digit
                                    code it provides.
                                </p>
                                <QRCodeSVG
                                    value={totpSetup.qrCodeUrl}
                                    size={200}
                                />
                            </div>

                            <VerifyMfaForm
                                methods={codeMethods}
                                onSubmit={onVerify}
                                isPending={enrolMutation.isPending}
                            />
                        </>
                    ) : (
                        <>
                            <div className="col-span-full">
                                <p className="text-sm/6">
                                    Set up an authenticator app such as Google
                                    Authenticator, Microsoft Authenticator or
                                    1Password.
                                </p>
                            </div>

                            <div className="col-span-full">
                                <Button
                                    onClick={onStartSetup}
                                    disabled={setMutation.isPending}
                                >
                                    {setMutation.isPending
                                        ? "Setting up..."
                                        : "Set up authenticator app"}
                                </Button>
                            </div>
                        </>
                    )}

                    <div className="col-span-full">
                        <Button variant="secondary" size="sm" asChild>
                            <Link to=".." relative="path">
                                {enrolled ? "Done" : "Go back"}
                            </Link>
                        </Button>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <ReauthenticateUser
            onReauthenticated={() => setReauthenticated(true)}
        />
    );
};
